import React, { useEffect, useRef, useState } from 'react';
import Appearance from 'styles/Appearance.js';
import Utils from 'files/Utils.js';
import Analytics from 'files/Analytics.js';
import Button from 'views/Button.js';
import SectionHeader from 'views/SectionHeader.js';
import Accordion from 'views/Accordion.js';
import QuizCTA from 'views/QuizCTA.js';
import Request from 'files/Request.js';
import Captcha from 'files/Captcha.js';

// Careers — the Aqua Link dealer recruiting page (/careers). Ported section for
// section from the Lovable dealer landing page, restyled in the storefront's
// section rhythm. Copy is Lovable's verified copy; keep claims as written.
//
// Leads post to the aftplatform recruiting endpoint: POST {config.url}/dealerships/
// with type 'new_shopify_recruiting_submission'. The backend only knows the original
// careers fields, so the dealer details are written into `message` and also sent
// as their own keys for when the backend learns to store them.

const BUSINESS_TYPES = [
    'Direct Sales',
    'Vacuum / Home Product Dealer',
    'Cookware Dealer',
    'Water Treatment',
    'Plumbing',
    'HVAC',
    'Restoration',
    'Other Home Services',
    'Other',
];

const SALES_MODELS = [
    'Direct-to-Home Sales',
    'Retail / Showroom',
    'Service-Based Business',
    'Distributor / Wholesale',
    'Independent Sales Representative',
    'Other',
];

const TEAM_SIZES = ['1', '2–5', '6–15', '16+'];

const MARKETS = ['Dallas–Fort Worth', 'Greater Houston', 'San Antonio', 'Waco & Central Texas'];

// Ad attribution — captured when the visitor lands and sent with the lead
const ATTRIBUTION_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term', 'gclid', 'fbclid'];
const ATTRIBUTION_STORE = 'aql_dealer_attribution';

const getAttribution = () => {
    try {
        return JSON.parse(window.sessionStorage.getItem(ATTRIBUTION_STORE) || '{}');
    } catch(e) {
        return {};
    }
};

const captureAttribution = () => {
    try {
        let existing = getAttribution();
        let params = new URLSearchParams(window.location.search);
        let found = {};
        ATTRIBUTION_KEYS.forEach(key => {
            let value = params.get(key);
            if(value) {
                found[key] = value.slice(0, 300);
            }
        });
        window.sessionStorage.setItem(ATTRIBUTION_STORE, JSON.stringify({
            ...existing,
            ...found,
            referrer: existing.referrer ?? document.referrer.slice(0, 500),
            landing_url: existing.landing_url ?? window.location.href.slice(0, 500),
        }));
    } catch(e) {
        // storage blocked (private mode) — the lead still submits without attribution
    }
};

const DealerProgramPage = ({ onNavigate, utils }) => {

    const mobile = Utils.isMobile();
    const stacked = mobile || Utils.isTablet();
    const columns = (desktop) => (mobile ? '1fr' : (Utils.isTablet() ? 'repeat(2, 1fr)' : desktop));

    useEffect(() => {
        window.scrollTo(0, 0);
        captureAttribution();
        Captcha.load().catch(() => {});
        let previousTitle = document.title;
        document.title = 'Become an Aqua Link Dealer | Smart Water Leak Detection';
        return () => { document.title = previousTitle; };
    }, []);

    const scrollToApply = (location) => {
        Analytics.dealerCtaClick({ location });
        Utils.scrollToElement('apply');
    };

    const applyButton = (label, location) => (
        <Button label={label} type={'primary'} onClick={() => scrollToApply(location)} style={{ padding: '16px 40px', fontSize: 16 }} />
    );

    const section = (background, children, props = {}) => (
        <section {...props} style={{ width: '100%', backgroundColor: background, scrollMarginTop: Appearance.layout.headerOffset() }}>
            <div style={{
                maxWidth: Appearance.layout.maxWidth,
                margin: '0 auto',
                padding: Appearance.layout.sectionPadding(),
            }}>
                {children}
            </div>
        </section>
    );

    const benefits = [
        {
            icon: 'fas fa-layer-group',
            title: 'Expand Your Product Offering',
            text: 'Add smart water leak detection to your existing home protection or smart home solutions.',
        },
        {
            icon: 'fas fa-shield-alt',
            title: 'Help Protect Your Customers’ Homes',
            text: 'Offer technology designed to help homeowners detect potential leaks and respond before damage escalates.',
        },
        {
            icon: 'fas fa-handshake',
            title: 'Explore a New Business Opportunity',
            text: 'Discover how Aqua Link products could complement your existing business and customer relationships.',
        },
    ];

    const industries = [
        { icon: 'fas fa-briefcase', title: 'Direct Sales Organizations', text: 'Expand your product portfolio with home water protection solutions.' },
        { icon: 'fas fa-home', title: 'Vacuum & Home Product Dealers', text: 'Introduce another valuable product category to your existing customers.' },
        { icon: 'fas fa-tint', title: 'Water Treatment Dealers', text: 'Complement your water treatment offerings with leak detection technology.' },
        { icon: 'fas fa-wrench', title: 'Plumbing & HVAC Professionals', text: 'Provide customers with additional options for protecting their homes.' },
        { icon: 'fas fa-building', title: 'Restoration Companies', text: 'Offer proactive water leak detection solutions alongside your existing services.' },
        { icon: 'fas fa-user', title: 'Independent Entrepreneurs', text: 'Explore how smart water leak detection could complement your business.' },
    ];

    const steps = [
        {
            icon: 'fas fa-clipboard-check',
            title: 'Submit Your Inquiry',
            text: 'Tell us about your business and your interest in becoming an Aqua Link dealer.',
        },
        {
            icon: 'fas fa-phone-alt',
            title: 'Explore the Opportunity',
            text: 'Connect with the Aqua Link team to learn about the product offering, program requirements, and potential partnership.',
        },
        {
            icon: 'fas fa-handshake',
            title: 'Review Next Steps',
            text: 'Determine whether the dealer opportunity is a good fit and discuss the onboarding process.',
        },
    ];

    const questions = [
        'What products or services does your business offer?',
        'Who are your customers, and where do you serve them?',
        'Have you worked with home services or smart home products?',
        'What are Aqua Link’s current dealer requirements?',
    ];

    const protection = [
        { icon: 'fas fa-water', label: 'Proactive Water Leak Detection' },
        { icon: 'fas fa-phone-alt', label: 'App, Text Message, and Email Alerts' },
        { icon: 'fas fa-layer-group', label: 'Additional Product Offering for Your Customers' }
    ];

    const technology = [
        { title: 'Water & Freeze Detection', text: 'Sensors detect water contact and freezing conditions near sinks, appliances and water heaters.' },
        { title: 'Connected Alerts', text: 'The Smart Hub sends app, text and email alerts to homeowners and up to 8 emergency contacts.' },
        { title: 'Bedside Control Center', text: 'An optional bedside alert unit with vibration helps alert homeowners while they sleep.' },
        { title: 'No Monthly Monitoring Fees', text: 'Aqua Link’s homeowner system provides connected alerts without an ongoing monitoring subscription.' },
    ];

    const faqs = [
        {
            q: 'Who can become an Aqua Link dealer?',
            a: 'Businesses and professionals interested in adding smart water leak detection can submit an inquiry. The Aqua Link team will review your business and discuss whether the opportunity is a good fit.',
        },
        {
            q: 'How does the Aqua Link leak detection system work?',
            a: 'Wireless Water Sensors detect water contact and freezing conditions. Connected through the Smart Hub, the system provides an on-device alarm plus app, text and email alerts to help homeowners locate the issue and respond.',
        },
        {
            q: 'What happens after I submit my inquiry?',
            a: 'The Aqua Link team will review your business information and contact you to explore the opportunity, answer questions and discuss next steps.',
        },
    ];

    const whiteOnDark = (alpha) => Utils.hexToRGBA(Appearance.colors.white, alpha);

    return (
        <div style={{ backgroundColor: Appearance.colors.white }}>

            {/* Hero — copy and CTA first so the apply button lands on a phone's first screen */}
            <section style={{
                width: '100%',
                paddingTop: Appearance.layout.headerOffset(),
            }}>
                <div style={{
                    maxWidth: 980,
                    margin: '0 auto',
                    padding: mobile ? '40px 20px 64px' : '72px 40px 96px',
                    textAlign: 'center',
                }}>
                    <span style={{ ...Appearance.textStyles.label(), display: 'block', marginBottom: 16 }}>
                        {'Aqua Link Dealer Opportunity'}
                    </span>
                    <h1 style={{
                        ...Appearance.textStyles.heroHeading(mobile ? { fontSize: 38 } : { fontSize: 60 }),
                        margin: '0 auto 22px',
                        textWrap: 'balance',
                    }}>
                        {'Expand Your Business With '}
                        <span style={{ color: Appearance.colors.primary() }}>{'Smart Water Leak Detection'}</span>
                    </h1>
                    <p style={{
                        ...Appearance.textStyles.body({ fontSize: mobile ? 17 : 20, lineHeight: 1.5 }),
                        maxWidth: '46ch',
                        margin: '0 auto',
                    }}>
                        {'Become an Aqua Link dealer and bring connected water protection solutions to your customers. Explore the opportunity to expand your product offering with smart leak detection technology.'}
                    </p>

                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: mobile ? 18 : 26,
                        marginTop: 30,
                        flexWrap: 'wrap',
                    }}>
                        <Button label={'Become an Aqua Link Dealer'} type={'primary'} onClick={() => scrollToApply('hero')} />
                        <span
                            onClick={() => Utils.scrollToElement('dealer-technology')}
                            style={{
                                color: Appearance.colors.primary(),
                                fontFamily: Appearance.fonts.body,
                                fontSize: 17,
                                fontWeight: 500,
                                cursor: 'pointer',
                            }}>
                            {'Explore the Technology ›'}
                        </span>
                    </div>

                    {/* Quiet proof points */}
                    <div style={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: mobile ? '10px 18px' : 28,
                        marginTop: 22,
                    }}>
                        {[
                            { icon: 'fas fa-tint', label: 'Smart Leak Detection' },
                            { icon: 'fas fa-phone-alt', label: 'Connected Alerts' },
                            { icon: 'fa-solid fa-piggy-bank', label: 'No Monthly Fees' },
                        ].map((point, index) => (
                            <span key={index} style={{
                                ...Appearance.textStyles.caption({ color: Appearance.colors.text(), fontWeight: 600 }),
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: 8,
                            }}>
                                <i className={point.icon} style={{ fontSize: 12, color: Appearance.colors.primary() }} />
                                {point.label}
                            </span>
                        ))}
                    </div>

                    <div style={{
                        marginTop: mobile ? 36 : 56,
                        borderRadius: 28,
                        overflow: 'hidden',
                        backgroundColor: Appearance.colors.offWhite,
                        aspectRatio: mobile ? '4 / 3' : '16 / 9',
                    }}>
                        <img
                            src={'/images/dealer-van.webp'}
                            alt={'A service professional in a navy polo shirt standing in front of a work van in a suburban neighborhood'}
                            fetchpriority={'high'}
                            style={{
                                width: '100%',
                                height: '100%',
                                objectFit: 'cover',
                                // keep the dealer in frame when phones crop the photo narrower
                                objectPosition: '62% center',
                            }} />
                    </div>
                </div>
            </section>

            {/* Inquiry form — sits directly under the hero */}
            {section(Appearance.colors.offWhite, (
                <div style={{
                    display: 'grid',
                    gridTemplateColumns: stacked ? '1fr' : '5fr 7fr',
                    gap: mobile ? 36 : 64,
                    alignItems: 'start',
                }}>
                    <div>
                        <SectionHeader
                            align={'left'}
                            eyebrow={'Dealer Inquiry'}
                            title={'Interested in Becoming an Aqua Link Dealer?'}
                            subtitle={'Tell us a little about yourself and your business. Our team will review your information and follow up about the next steps.'}
                            style={{ marginBottom: mobile ? 28 : 36 }} />

                        <span style={{ ...Appearance.textStyles.label({ color: Appearance.colors.text() }), display: 'block', marginBottom: 16 }}>
                            {'What happens after you submit'}
                        </span>
                        <ol style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
                            {[
                                ['We review your inquiry', 'A member of the Aqua Link team looks over your business information.'],
                                ['We reach out personally', 'A representative contacts you to walk through the program and answer questions.'],
                                ['You decide', 'Discuss whether the opportunity fits your business and review the onboarding process.'],
                            ].map(([title, text], index) => (
                                <li key={index} style={{ display: 'flex', gap: 14 }}>
                                    <NumberBadge number={index + 1} size={28} />
                                    <span style={Appearance.textStyles.body({ fontSize: 15 })}>
                                        <span style={{ color: Appearance.colors.text(), fontWeight: 600 }}>{`${title}. `}</span>
                                        {text}
                                    </span>
                                </li>
                            ))}
                        </ol>
                    </div>

                    <div style={{
                        backgroundColor: Appearance.colors.white,
                        borderRadius: 24,
                        boxShadow: Appearance.boxShadow.card,
                        padding: mobile ? '28px 20px' : 40,
                    }}>
                        <DealerForm utils={utils} />
                    </div>
                </div>
            ), { id: 'apply' })}

            {/* Why partner */}
            {section(Appearance.colors.white, (
                <>
                    <SectionHeader
                        eyebrow={'Why Partner With Aqua Link'}
                        title={'More Than Another Product to Sell'}
                        subtitle={'Whether you’re expanding your existing product offerings or exploring a new sales opportunity, Aqua Link gives you a way to introduce home water protection solutions to your customers.'} />
                    <div style={{ display: 'grid', gridTemplateColumns: columns('repeat(3, 1fr)'), gap: mobile ? 20 : 24 }}>
                        {benefits.map((item, index) => (
                            <IconCard key={index} {...item} />
                        ))}
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'center', marginTop: mobile ? 36 : 48 }}>
                        {applyButton('Apply Now', 'why')}
                    </div>
                </>
            ), { maxWidth: 1400 })}

            {/* Who it's for */}
            {section(Appearance.colors.offWhite, (
                <>
                    <SectionHeader
                        eyebrow={'Who We’re Looking For'}
                        title={'Is Aqua Link the Right Fit for Your Business?'}
                        subtitle={'We’re looking for motivated businesses and sales professionals interested in bringing home water protection solutions to more customers.'} />
                    <div style={{ display: 'grid', gridTemplateColumns: columns('repeat(3, 1fr)'), gap: mobile ? 16 : 24 }}>
                        {industries.map((item, index) => (
                            <IconCard key={index} {...item} />
                        ))}
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'center', marginTop: 24 }}>
                        {applyButton('Explore Opportunities', 'fit')}
                    </div>
                </>
            ))}

            {/* How the dealer process works */}
            {section(Appearance.colors.white, (
                <>
                    <SectionHeader
                        eyebrow={'The Dealer Opportunity'}
                        title={'How the Dealer Process Works'}
                        subtitle={'Start a conversation, understand the requirements, then review the next steps.'} />
                    <ol style={{
                        position: 'relative',
                        display: 'grid',
                        gridTemplateColumns: mobile ? '1fr' : 'repeat(3, 1fr)',
                        gap: mobile ? 40 : 32,
                    }}>
                        {/* Connector running behind the step icons */}
                        {!mobile && (
                            <div aria-hidden={true} style={{
                                position: 'absolute',
                                top: 28,
                                left: '16.66%',
                                right: '16.66%',
                                height: 2,
                                backgroundColor: Appearance.colors.border(),
                            }} />
                        )}
                        {steps.map((step, index) => (
                            <li key={index} style={{ position: 'relative', textAlign: 'center' }}>
                                <div style={{
                                    width: 56,
                                    height: 56,
                                    borderRadius: 28,
                                    margin: '0 auto',
                                    backgroundColor: Appearance.colors.primary(),
                                    boxShadow: `0 0 0 8px #D3DFEA, ${Appearance.boxShadow.button}`,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                }}>
                                    <i className={step.icon} style={{ fontSize: 21, color: Appearance.colors.white }} />
                                </div>
                                <span style={{ ...Appearance.textStyles.label(), display: 'block', marginTop: 20 }}>
                                    {`Step ${index + 1}`}
                                </span>
                                <h3 style={{ ...Appearance.textStyles.subHeading({ fontSize: 19 }), marginTop: 6 }}>
                                    {step.title}
                                </h3>
                                <p style={{ ...Appearance.textStyles.body({ fontSize: 15 }), maxWidth: 320, margin: '8px auto 0' }}>
                                    {step.text}
                                </p>
                            </li>
                        ))}
                    </ol>
                    <p style={{ ...Appearance.textStyles.caption({ textAlign: 'center' }), marginTop: mobile ? 36 : 48 }}>
                        {'All applicants are reviewed individually. Submitting an inquiry does not guarantee dealer approval.'}
                    </p>
                </>
            ))}

            {/* Qualifications — what the first conversation covers */}
            {/*section(Appearance.colors.offWhite, (
                <div style={{
                    display: 'grid',
                    gridTemplateColumns: stacked ? '1fr' : '1fr 1fr',
                    gap: mobile ? 32 : 64,
                    alignItems: 'start',
                }}>
                    <div>
                        <SectionHeader
                            align={'left'}
                            eyebrow={'Let’s Talk About Your Business'}
                            title={'Is Aqua Link a Fit for Your Business?'}
                            subtitle={'Start with an inquiry. You can discuss your business with the Aqua Link team and ask about current dealer requirements before deciding whether to move forward.'}
                            style={{ marginBottom: mobile ? 24 : 32 }} />
                        {applyButton('Start the Process', 'qualifications')}
                    </div>
                    <ul style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                        {questions.map((question, index) => (
                            <li key={index} style={{
                                ...Appearance.styles.card(),
                                boxShadow: Appearance.boxShadow.card,
                                display: 'flex',
                                alignItems: 'center',
                                gap: 16,
                                padding: mobile ? '16px 18px' : '18px 22px',
                            }}>
                                <i className={'fa-solid fa-circle-question'} aria-hidden={true} style={{ fontSize: 16, color: Appearance.colors.primary(), flexShrink: 0 }} />
                                <span style={Appearance.textStyles.body({ color: Appearance.colors.text(), fontWeight: 500, fontSize: 15 })}>
                                    {question}
                                </span>
                            </li>
                        ))}
                    </ul>
                </div>
            ))*/}

            {/* Why home water protection */}
            {section(Appearance.colors.offWhite, (
                <div style={{
                    display: 'grid',
                    gridTemplateColumns: stacked ? '1fr' : '1fr 1fr',
                    gap: mobile ? 36 : 64,
                    alignItems: 'center',
                }}>
                    <div style={{
                        borderRadius: 28,
                        overflow: 'hidden',
                        backgroundColor: Appearance.colors.offWhite,
                        aspectRatio: '4 / 3',
                    }}>
                        <img
                            src={'/images/water-sensor-front-white.jpg'}
                            alt={'Actual Aqua Link Water Sensor with water-contact sensing strips'}
                            loading={'lazy'}
                            style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                    </div>
                    <div>
                        <SectionHeader
                            align={'left'}
                            eyebrow={'Why Home Water Protection'}
                            title={'Help Customers Protect What Matters Most'}
                            subtitle={'Water leaks can cause unexpected damage and costly disruptions. Aqua Link connects water leak sensors with alerts that help homeowners identify potential problems and respond. Dealers can add this protection to their existing offerings.'}
                            style={{ marginBottom: mobile ? 24 : 32 }} />
                        <ul style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                            {protection.map((item, index) => (
                                <li key={index} style={{
                                    ...Appearance.styles.card(),
                                    boxShadow: Appearance.boxShadow.card,
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 16,
                                    padding: mobile ? '16px 18px' : '18px 22px',
                                }}>
                                    <i className={item.icon} style={{ fontSize: 17, color: Appearance.colors.primary(), flexShrink: 0, width: 22, textAlign: 'center' }} />
                                    <span style={Appearance.textStyles.subHeading({ fontSize: 16 })}>
                                        {item.label}
                                    </span>
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>
            ))}

            {/* Technology — the storefront's dark surface (same as the closing CTA) */}
            <section id={'dealer-technology'} style={{
                width: '100%',
                background: 'linear-gradient(160deg, #24242a 0%, #141417 100%)',
                scrollMarginTop: Appearance.layout.headerOffset(),
            }}>
                <div style={{
                    maxWidth: Appearance.layout.maxWidth,
                    margin: '0 auto',
                    padding: Appearance.layout.sectionPadding(),
                }}>
                    <div style={{ textAlign: 'center', maxWidth: 725, margin: '0 auto', marginBottom: mobile ? 36 : 48 }}>
                        <span style={{ ...Appearance.textStyles.label(), display: 'block', marginBottom: 14 }}>
                            {'Aqua Link Technology'}
                        </span>
                        <h2 style={{
                            ...Appearance.textStyles.sectionHeading({ color: Appearance.colors.white }),
                            marginBottom: 16,
                            textWrap: 'balance',
                        }}>
                            {'Connected Water Protection. Clear Alerts.'}
                        </h2>
                        <p style={Appearance.textStyles.sectionSubheading({ color: whiteOnDark(0.72), margin: '0 auto' })}>
                            {'Wireless Water Sensors monitor the places where leaks can start. The Smart Hub connects the network, delivering app, text and email alerts so homeowners can identify the location and take action.'}
                        </p>
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: columns('repeat(4, 1fr)'), gap: mobile ? 16 : 20 }}>
                        {technology.map((item, index) => (
                            <div key={index} style={{
                                borderRadius: 16,
                                border: `1px solid ${whiteOnDark(0.14)}`,
                                backgroundColor: whiteOnDark(0.05),
                                padding: mobile ? 22 : 26,
                            }}>
                                <i className={'fas fa-check-circle'} style={{ fontSize: 20, color: Appearance.colors.primary() }} />
                                <h3 style={{ ...Appearance.textStyles.subHeading({ fontSize: 17, color: Appearance.colors.white }), marginTop: 14 }}>
                                    {item.title}
                                </h3>
                                <p style={{ ...Appearance.textStyles.body({ fontSize: 14, color: whiteOnDark(0.68) }), marginTop: 8 }}>
                                    {item.text}
                                </p>
                            </div>
                        ))}
                    </div>
                    <p style={{ ...Appearance.textStyles.caption({ color: whiteOnDark(0.6), textAlign: 'center' }), marginTop: mobile ? 32 : 44 }}>
                        {'Devices designed and engineered in the U.S.A. '}
                        <span
                            onClick={() => onNavigate && onNavigate({ section: 'products' })}
                            style={{ color: whiteOnDark(0.92), fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' }}>
                            {'Explore Our Technology →'}
                        </span>
                    </p>
                </div>
            </section>

            {/* About */}
            <section style={{ width: '100%' }}>
                <div style={{
                    maxWidth: Appearance.layout.readableWidth,
                    margin: '0 auto',
                    padding: Appearance.layout.sectionPadding(),
                    textAlign: 'center',
                }}>
                    <SectionHeader
                        eyebrow={'About Aqua Link'}
                        title={'Smart Water Protection for the Homes You Serve'}
                        subtitle={'Aqua Link develops sensors and alert systems for connected home protection. Its water detection network helps homeowners, renters and building managers identify leaks and freezing conditions. Explore how this product category could complement your existing customer relationships.'}
                        style={{ marginBottom: mobile ? 28 : 40 }} />
                    <div style={{
                        borderRadius: 28,
                        overflow: 'hidden',
                        backgroundColor: Appearance.colors.offWhite,
                        aspectRatio: '1984 / 1179',
                    }}>
                        <img
                            src={'/images/starter-pack-white-cropped.jpg'}
                            alt={'Actual Aqua Link system with Water Sensors, Smart Hub and Bedside Control Center'}
                            loading={'lazy'}
                            style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    </div>
                </div>
            </section>

            {/* Markets */}
            {/*section(Appearance.colors.offWhite, (
                <>
                    <SectionHeader
                        eyebrow={'Dealer Recruitment Markets'}
                        title={'Tell Us Where You Serve'}
                        subtitle={'Share your service area when you inquire so the team can follow up with you there.'} />
                    <div style={{ display: 'grid', gridTemplateColumns: mobile ? '1fr 1fr' : 'repeat(4, 1fr)', gap: mobile ? 12 : 20 }}>
                        {MARKETS.map((market, index) => (
                            <div key={index} style={{
                                ...Appearance.styles.card(),
                                display: 'flex',
                                flexDirection: mobile ? 'column' : 'row',
                                alignItems: mobile ? 'flex-start' : 'center',
                                gap: mobile ? 10 : 14,
                                padding: mobile ? 18 : 24,
                            }}>
                                <i className={'fas fa-map-marker-alt'} style={{ fontSize: 17, color: Appearance.colors.primary(), flexShrink: 0 }} />
                                <span style={Appearance.textStyles.subHeading({ fontSize: mobile ? 15 : 17 })}>
                                    {market}
                                </span>
                            </div>
                        ))}
                    </div>
                    <p style={{ ...Appearance.textStyles.body({ textAlign: 'center' }), marginTop: mobile ? 28 : 36 }}>
                        {'These Texas markets are where we’re recruiting now. Businesses elsewhere are welcome to inquire.'}
                    </p>
                </>
            ))*/}

            {/* FAQ */}
            <section style={{ backgroundColor: Appearance.colors.offWhite, width: '100%' }}>
                <div style={{
                    maxWidth: Appearance.layout.readableWidth,
                    margin: '0 auto',
                    padding: Appearance.layout.sectionPadding(),
                }}>
                    <SectionHeader eyebrow={'FAQ'} title={'Frequently Asked Questions'} />
                    <Accordion items={faqs} />
                </div>
            </section>

            {/* Closing CTA */}
            <QuizCTA
                onOpenQuiz={() => scrollToApply('final_cta')}
                heading={'Your Next Business Opportunity Starts Here.'}
                subtext={'Discover how becoming an Aqua Link dealer could help you expand your product offerings and build new sales opportunities.'}
                buttonLabel={'Become an Aqua Link Dealer'}
                buttonIcon={'fas fa-handshake'} />

            <StickyApplyBar onApply={() => scrollToApply('sticky_mobile')} />
        </div>
    );
};

const NumberBadge = ({ number, size }) => (
    <span style={{
        width: size,
        height: size,
        borderRadius: size / 2,
        flexShrink: 0,
        backgroundColor: Appearance.colors.primary(),
        color: Appearance.colors.white,
        fontFamily: Appearance.fonts.body,
        fontSize: 13,
        fontWeight: 700,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    }}>
        {number}
    </span>
);

// Bordered card with an icon-led title — the card system used by How It Works
const IconCard = ({ icon, title, text }) => (
    <div style={{
        ...Appearance.styles.card(),
        boxShadow: Appearance.boxShadow.card,
        padding: Utils.isMobile() ? 24 : 28,
    }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 10 }}>
            <i className={icon} style={{ fontSize: 19, color: Appearance.colors.primary(), flexShrink: 0, width: 22, textAlign: 'center' }} />
            <h3 style={{ ...Appearance.textStyles.subHeading({ fontSize: 18 }), margin: 0 }}>
                {title}
            </h3>
        </div>
        <p style={Appearance.textStyles.body({ fontSize: 15 })}>
            {text}
        </p>
    </div>
);

// Phones only: keeps the apply button in reach once the hero CTA scrolls away,
// and steps aside while the form itself is on screen.
const StickyApplyBar = ({ onApply }) => {

    const [visible, setVisible] = useState(false);

    useEffect(() => {
        if(!Utils.isMobile() || typeof IntersectionObserver === 'undefined') {
            return;
        }
        let apply = document.getElementById('apply');
        if(!apply) {
            return;
        }
        let applyInView = false;
        const update = () => setVisible(window.scrollY > 600 && !applyInView);
        const observer = new IntersectionObserver(([entry]) => {
            applyInView = entry.isIntersecting;
            update();
        });
        observer.observe(apply);
        window.addEventListener('scroll', update, { passive: true });
        return () => {
            observer.disconnect();
            window.removeEventListener('scroll', update);
        };
    }, []);

    if(!Utils.isMobile()) {
        return null;
    }
    return (
        <div style={{
            position: 'fixed',
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 90,
            padding: '12px 20px calc(12px + env(safe-area-inset-bottom, 0px))',
            backgroundColor: 'rgba(255,255,255,0.97)',
            backdropFilter: 'blur(20px)',
            borderTop: `1px solid ${Appearance.colors.border()}`,
            borderBottom: `1px solid ${Appearance.colors.border()}`,
            transform: visible ? 'translateY(0)' : 'translateY(110%)',
            // hidden, not just off-screen, so the button can't take keyboard focus while tucked away
            visibility: visible ? 'visible' : 'hidden',
            transition: visible ? 'transform 0.3s ease' : 'transform 0.3s ease, visibility 0s linear 0.3s',
        }}>
            <Button label={'Become an Aqua Link Dealer'} type={'primary'} onClick={onApply} style={{ width: '100%', fontSize: 16 }} />
        </div>
    );
};

const emptyForm = {
    name: '',
    phone: '',
    email: '',
    company: '',
    businessType: '',
    salesModel: '',
    teamSize: '',
    zip: '',
    consent: false,
};

const validate = (form) => {
    let errors = {};
    if(form.name.trim().length < 2) {
        errors.name = 'Please enter your full name';
    }
    if(!/^[+()\-.\s\d]{7,20}$/.test(form.phone.trim())) {
        errors.phone = 'Please enter a valid phone number';
    }
    if(!Utils.validateEmail(form.email.trim())) {
        errors.email = 'Please enter a valid email';
    }
    if(form.zip.trim() && !/^\d{5}(-\d{4})?$/.test(form.zip.trim())) {
        errors.zip = 'Please enter a valid ZIP code';
    }
    if(!form.consent) {
        errors.consent = 'Please agree to be contacted';
    }
    return errors;
};

// The backend stores free text in `message`; one labeled line per detail the dealer gave us
const buildMessage = (form, attribution) => {
    let campaign = ['utm_source', 'utm_medium', 'utm_campaign'].map(key => attribution[key]).filter(Boolean).join(' / ');
    return [
        'Dealer inquiry (/careers)',
        form.company && `Business name: ${form.company.trim()}`,
        form.businessType && `Business type: ${form.businessType}`,
        form.salesModel && `Sales model: ${form.salesModel}`,
        form.teamSize && `Team size: ${form.teamSize}`,
        form.zip && `Business ZIP: ${form.zip.trim()}`,
        'Agreed to be contacted by phone, text or email: yes',
        campaign && `Campaign: ${campaign}`,
    ].filter(Boolean).join('\n');
};

const DealerForm = ({ utils }) => {

    const [form, setForm] = useState(emptyForm);
    const [errors, setErrors] = useState({});
    const [submitting, setSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [error, setError] = useState(null);
    const started = useRef(false);

    const set = (key, value) => {
        if(!started.current) {
            started.current = true;
            Analytics.dealerFormStart();
        }
        setForm(current => ({ ...current, [key]: value }));
        if(errors[key]) {
            setErrors(current => ({ ...current, [key]: undefined }));
        }
    };

    const onSubmit = async (e) => {
        e.preventDefault();
        if(submitting) {
            return;
        }
        let found = validate(form);
        if(Object.keys(found).length > 0) {
            setErrors(found);
            return;
        }
        setSubmitting(true);
        setError(null);
        try {
            let attribution = getAttribution();
            await Request.post(utils, '/dealerships/', {
                // the server checks the token's action against this submission type
                captcha_token: await Captcha.getToken('careers'),
                city_state: form.zip.trim(),
                email_address: form.email.trim(),
                full_name: form.name.trim(),
                message: buildMessage(form, attribution),
                phone_number: form.phone.trim(),
                type: 'new_shopify_recruiting_submission',
                business_name: form.company.trim(),
                business_type: form.businessType,
                contact_consent: true,
                sales_model: form.salesModel,
                source: 'dealer_program',
                team_size: form.teamSize,
                zip_code: form.zip.trim(),
                ...attribution,
            });
            Analytics.dealerLeadSubmit({ businessType: form.businessType, salesModel: form.salesModel, teamSize: form.teamSize });
            setSubmitted(true);
        } catch(err) {
            setError(err.message || 'Something went wrong. Please try again.');
        } finally {
            setSubmitting(false);
        }
    };

    if(submitted) {
        return (
            <div role={'status'} style={{ textAlign: 'center', padding: Utils.isMobile() ? '32px 4px' : '48px 24px' }}>
                <div style={{
                    width: 56, height: 56, borderRadius: '50%',
                    backgroundColor: Utils.hexToRGBA(Appearance.colors.success, 0.15),
                    display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 18px',
                }}>
                    <i className={'fas fa-check'} style={{ fontSize: 22, color: Appearance.colors.success }} />
                </div>
                <h3 style={{ ...Appearance.textStyles.heading({ fontSize: 24 }), marginBottom: 10 }}>{'Thank you — your inquiry is in.'}</h3>
                <p style={{ ...Appearance.textStyles.body(), maxWidth: 420, margin: '0 auto' }}>
                    {'An Aqua Link representative will review your information and reach out to discuss the dealer opportunity and next steps in the application process.'}
                </p>
            </div>
        );
    }

    const fieldStyle = (key) => ({
        fontFamily: Appearance.fonts.body,
        fontSize: 16, // 16px keeps iOS from zooming into the field
        padding: '13px 16px',
        borderRadius: 12,
        border: `1px solid ${errors[key] ? Appearance.colors.error : Appearance.colors.border()}`,
        backgroundColor: Appearance.colors.white,
        color: Appearance.colors.text(),
        outline: 'none',
        width: '100%',
        appearance: 'none',
        WebkitAppearance: 'none',
    });

    const errorText = (key) => errors[key] && (
        <span id={`dealer-${key}-error`} style={{ ...Appearance.textStyles.caption({ color: Appearance.colors.error }), display: 'block', marginTop: 6 }}>
            {errors[key]}
        </span>
    );

    const label = (key, text, optional) => (
        <label htmlFor={`dealer-${key}`} style={{ ...Appearance.textStyles.caption({ color: Appearance.colors.text(), fontWeight: 600 }), display: 'block', marginBottom: 8 }}>
            {text}
            {optional
                ? <span style={{ color: Appearance.colors.lightGrey, fontWeight: 500 }}>{' (optional)'}</span>
                : <span aria-hidden={true} style={{ color: Appearance.colors.primary(), marginLeft: 2 }}>{'*'}</span>}
        </label>
    );

    const input = (key, text, { type = 'text', optional, autoComplete, inputMode } = {}) => (
        <div>
            {label(key, text, optional)}
            <input
                id={`dealer-${key}`}
                type={type}
                inputMode={inputMode}
                autoComplete={autoComplete}
                value={form[key]}
                onChange={(e) => set(key, e.target.value)}
                aria-invalid={!!errors[key]}
                aria-describedby={errors[key] ? `dealer-${key}-error` : undefined}
                style={fieldStyle(key)} />
            {errorText(key)}
        </div>
    );

    const select = (key, text, options, placeholder) => (
        <div>
            {label(key, text, true)}
            <div style={{ position: 'relative' }}>
                <select
                    id={`dealer-${key}`}
                    value={form[key]}
                    onChange={(e) => set(key, e.target.value)}
                    style={{ ...fieldStyle(key), paddingRight: 40, color: form[key] ? Appearance.colors.text() : Appearance.colors.lightGrey }}>
                    <option value={''}>{placeholder}</option>
                    {options.map(option => (
                        <option key={option} value={option}>{option}</option>
                    ))}
                </select>
                <i className={'fas fa-chevron-down'} style={{
                    position: 'absolute',
                    right: 16,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    fontSize: 11,
                    color: Appearance.colors.lightGrey,
                    pointerEvents: 'none',
                }} />
            </div>
        </div>
    );

    return (
        <form onSubmit={onSubmit} noValidate style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
            <div style={{ display: 'grid', gridTemplateColumns: Utils.isMobile() ? '1fr' : '1fr 1fr', gap: 18 }}>
                {input('name', 'Full Name', { autoComplete: 'name' })}
                {input('phone', 'Mobile Phone', { type: 'tel', inputMode: 'tel', autoComplete: 'tel' })}
                {input('email', 'Email Address', { type: 'email', autoComplete: 'email' })}
                {input('company', 'Business Name', { optional: true, autoComplete: 'organization' })}
                {select('businessType', 'Business Type', BUSINESS_TYPES, 'Select industry')}
                {select('salesModel', 'Sales Model', SALES_MODELS, 'Select sales model')}
                {select('teamSize', 'Team Size', TEAM_SIZES, 'Select team size')}
                {input('zip', 'Business ZIP Code', { optional: true, inputMode: 'numeric', autoComplete: 'postal-code' })}
            </div>

            <div style={{ backgroundColor: Appearance.colors.offWhite, borderRadius: 12, padding: 16 }}>
                <label htmlFor={'dealer-consent'} style={{ display: 'flex', alignItems: 'flex-start', gap: 12, cursor: 'pointer' }}>
                    <input
                        id={'dealer-consent'}
                        type={'checkbox'}
                        checked={form.consent}
                        onChange={(e) => set('consent', e.target.checked)}
                        aria-invalid={!!errors.consent}
                        aria-describedby={errors.consent ? 'dealer-consent-error' : undefined}
                        style={{ width: 18, height: 18, marginTop: 2, flexShrink: 0, accentColor: Appearance.colors.primary() }} />
                    <span style={Appearance.textStyles.caption({ color: Appearance.colors.subText(), fontWeight: 400, lineHeight: 1.5 })}>
                        {'I agree to be contacted by Aqua Link by phone, text, or email about the dealer opportunity. Message and data rates may apply.'}
                    </span>
                </label>
                {errorText('consent')}
            </div>

            {error && (
                <span role={'alert'} style={Appearance.textStyles.caption({ color: Appearance.colors.error })}>{error}</span>
            )}

            <Button label={'Start the Process'} type={'primary'} loading={submitting} onClick={() => {}} style={{ width: '100%', padding: '16px 32px', fontSize: 16 }} />
            <p style={{ ...Appearance.textStyles.caption(), fontSize: 12, textAlign: 'center' }}>
                {'Your information will be used to contact you regarding the Aqua Link dealer opportunity.'}
            </p>
        </form>
    );
};

export default DealerProgramPage;
